import {
    areSearchParamsEqual,
    QUALITY_CONTROL_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type QualityControlFilterItem = { key: string; value: string };

export type QualityControlUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    questionnaireId: number | null;
    allTimeChecked: boolean;
    filterQualityControlStatus: QualityControlFilterItem[] | null;
    filterStatus: QualityControlFilterItem[] | null;
    filterOwnerType: QualityControlFilterItem[] | null;
    filterForcedClosing: QualityControlFilterItem[] | null;
    filterBranchName: QualityControlFilterItem[] | null;
    filterIsManual: QualityControlFilterItem[] | null;
    filterQualityControlId: QualityControlFilterItem[] | null;
    filterFullName: QualityControlFilterItem[] | null;
    filterAni: QualityControlFilterItem | null;
    filterDateTime: Date | null;
    filterEndDateTime: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    hasCustomDateRange: boolean;
    controlEndFromDate: Date | null;
    controlEndToDate: Date | null;
    controlEndFromTime: Date | null;
    controlEndToTime: Date | null;
    hasControlEndFilter: boolean;
    uiFilters: Record<string, any>;
};

export type QualityControlUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    questionnaireId: number | null;
    allTimeChecked: boolean;
    filterQualityControlStatus: QualityControlFilterItem[] | null;
    filterStatus: QualityControlFilterItem[] | null;
    filterOwnerType: QualityControlFilterItem[] | null;
    filterForcedClosing: QualityControlFilterItem[] | null;
    filterBranchName: QualityControlFilterItem[] | null;
    filterIsManual: QualityControlFilterItem[] | null;
    filterQualityControlId: QualityControlFilterItem[] | null;
    filterFullName: QualityControlFilterItem[] | null;
    filterAni: QualityControlFilterItem | null;
    filterDateTime: any;
    filterEndDateTime: any;
    startTime: any;
    endTime: any;
    hasControlEndFilter: boolean;
    controlEndFromDate: any;
    controlEndToDate: any;
    controlEndFromTime: any;
    controlEndToTime: any;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialQualityControlUrlState(): QualityControlUrlViewState {
    return parseQualityControlUrlState(getHashSearchParams());
}

export function parseQualityControlUrlState(params: URLSearchParams): QualityControlUrlViewState {
    params = unwrapEncodedSearchParams(params, QUALITY_CONTROL_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const questionnaireId = parseOptionalInt(params.get('controlId'));
    const allTimeChecked = parseBooleanParam(params.get('allTime'), false);
    const qcStatus = getMultiParams(params, 'qcStatus').map(toNumberValue);
    const status = getMultiParams(params, 'status').map(toNumberValue);
    const ownerType = getMultiParams(params, 'ownerType').map(toNumberValue);
    const forcedClosing = getMultiParams(params, 'forcedClosing').map(toNumberValue);
    const branchId = getMultiParams(params, 'branchId').map(toNumberValue);
    const isManual = getMultiParams(params, 'isManual');
    const qcId = getMultiParams(params, 'qcId').map(toNumberValue);
    const fullName = getMultiParams(params, 'fullName');
    const ani = params.get('ani');
    const startRange = allTimeChecked
        ? getAllTimeRange()
        : parseDateTimeRange(params.get('startFrom'), params.get('startTo'));
    const controlEndRange = parseDateTimeRange(params.get('endFrom'), params.get('endTo'));

    if (qcStatus.length) uiFilters.qualityControlStatus = qcStatus;
    if (status.length) uiFilters.status = status;
    if (ownerType.length) uiFilters.ownerType = ownerType;
    if (forcedClosing.length) uiFilters.forcedClosing = forcedClosing;
    if (branchId.length) uiFilters.branchId = branchId;
    if (isManual.length) uiFilters.isManual = isManual;
    if (qcId.length) {
        uiFilters.qualityControlId = qcId;
        uiFilters.qualityControlName = qcId;
    }
    if (fullName.length) uiFilters.fullName = fullName;
    if (ani) uiFilters.ani = ani;
    if (controlEndRange) uiFilters.controlEnd = controlEndRange.startDate;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        questionnaireId,
        allTimeChecked,
        filterQualityControlStatus: toFilterList('QualityControlStatus', qcStatus),
        filterStatus: toFilterList('Status', status),
        filterOwnerType: toFilterList('OwnerType', ownerType),
        filterForcedClosing: toFilterList('ForcedClosing', forcedClosing),
        filterBranchName: toFilterList('BranchId', branchId),
        filterIsManual: toFilterList('IsManual', isManual),
        filterQualityControlId: toFilterList('QualityControlId', qcId),
        filterFullName: toFilterList('UserName', fullName),
        filterAni: toFilterItem('ANI', ani),
        filterDateTime: startRange?.startDate ?? null,
        filterEndDateTime: startRange?.endDate ?? null,
        startTime: startRange?.startTime ?? null,
        endTime: startRange?.endTime ?? null,
        hasCustomDateRange: !!startRange || allTimeChecked,
        controlEndFromDate: controlEndRange?.startDate ?? null,
        controlEndToDate: controlEndRange?.endDate ?? null,
        controlEndFromTime: controlEndRange?.startTime ?? null,
        controlEndToTime: controlEndRange?.endTime ?? null,
        hasControlEndFilter: !!controlEndRange,
        uiFilters,
    };
}

export function buildQualityControlSearchParams(state: QualityControlUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(
        buildPlainQualityControlSearchParams(state),
        QUALITY_CONTROL_ENCODED_QUERY_KEY
    );
}

function buildPlainQualityControlSearchParams(state: QualityControlUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }

    if (state.questionnaireId != null) {
        params.set('controlId', String(state.questionnaireId));
    }

    if (state.allTimeChecked) {
        params.set('allTime', '1');
    } else if (shouldWriteStartRange(state)) {
        const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
        const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
        if (startFrom) params.set('startFrom', toLocalIso(startFrom));
        if (startTo) params.set('startTo', toLocalIso(startTo));
    }

    setMulti(params, 'qcStatus', state.filterQualityControlStatus);
    setMulti(params, 'status', state.filterStatus);
    setMulti(params, 'ownerType', state.filterOwnerType);
    setMulti(params, 'forcedClosing', state.filterForcedClosing);
    setMulti(params, 'branchId', state.filterBranchName);
    setMulti(params, 'isManual', state.filterIsManual);
    setMulti(params, 'qcId', state.filterQualityControlId);
    setMulti(params, 'fullName', state.filterFullName);
    setScalar(params, 'ani', state.filterAni?.value);

    if (state.hasControlEndFilter && hasDateRange(state.controlEndFromDate, state.controlEndToDate, state.controlEndFromTime, state.controlEndToTime)) {
        const endFrom = combineDateAndTime(state.controlEndFromDate, state.controlEndFromTime, true);
        const endTo = combineDateAndTime(state.controlEndToDate, state.controlEndToTime, false);
        if (endFrom) params.set('endFrom', toLocalIso(endFrom));
        if (endTo) params.set('endTo', toLocalIso(endTo));
    }

    return params;
}

function shouldWriteStartRange(state: QualityControlUrlWriteState): boolean {
    const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
    const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
    if (!startFrom || !startTo) return false;
    if (state.questionnaireId != null) return true;
    return !isDefaultDateRange(startFrom, startTo);
}

function hasDateRange(startDate: any, endDate: any, startTime: any, endTime: any): boolean {
    return !!(startDate && endDate && startTime && endTime);
}

function isDefaultDateRange(startFrom: Date, startTo: Date): boolean {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 0);
    return toLocalIso(startFrom) === toLocalIso(todayStart) && toLocalIso(startTo) === toLocalIso(todayEnd);
}

function getAllTimeRange() {
    const startDate = new Date(2023, 5, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    const startTime = new Date();
    startTime.setHours(0, 0, 0, 0);
    const endTime = new Date();
    endTime.setHours(23, 59, 59, 0);
    return { startDate, endDate, startTime, endTime };
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: QualityControlFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value) {
            params.append(key, item.value);
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): QualityControlFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number>): QualityControlFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseOptionalInt(value: string | null): number | null {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return null;
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}

function toLocalIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function fromLocalIso(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
    );
}

function parseDateTimeRange(fromValue: string | null, toValue: string | null) {
    if (!fromValue || !toValue) return null;
    const from = fromLocalIso(fromValue);
    const to = fromLocalIso(toValue);
    if (!from || !to) return null;

    return {
        startDate: startOfDay(from),
        endDate: startOfDay(to),
        startTime: timeOnly(from),
        endTime: timeOnly(to),
    };
}

function startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function timeOnly(date: Date): Date {
    const next = new Date();
    next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return next;
}

function combineDateAndTime(dateValue: any, timeValue: any, isStart: boolean): Date | null {
    const date = toValidDate(dateValue);
    if (!date) return null;
    const time = toValidDate(timeValue);
    const result = new Date(date);
    if (time) {
        result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    } else {
        result.setHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, 0);
    }
    return result;
}

function toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
